import React, { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { courseschedulesApi } from "api";
import { useCourseCreate } from "../CourseCreateContext";
import { useCourseQuery } from "pages/CourseCreate/useCourseQuery";
import { useScheduleEvents } from "api/scheduleEvents";
import {
  CourseScheduleList,
  CourseSchedulePreview,
  CourseSchedulePreviewType,
} from "components/molecules/CourseSchedule";
import { CalendarEvent } from "pages/Dashboard/CalendarDayCard";
import { ScheduleModel } from "components/molecules/MonthCalendar";
import { EditCourseScheduleModal } from "./edit/EditCourseScheduleModal";
import { ButtonSwitch, PaziaBox } from "components/atoms";
import { MoreActionsMenu, PaziaButton } from "components/atoms";
import { CreateNewEventModal } from "./create-new/CreateNewEventModal";
import { BulkEditPlacesModal } from "./BulkEditPlacesModal";
import { ExportScheduleModal } from "./export/ExportScheduleModal";
import { ScheduleHistoryModal } from "./history/ScheduleHistoryModal";
import { PaziaDeleteModal } from "components/molecules";
import { useToaster } from "utils";
import { QUERIES_KEYS } from "queries";
import { describeConflict, SCHEDULE_CONFLICTS_KEY, useScheduleConflicts } from "./conflicts/scheduleConflicts";

interface CourseSchedulePreviewProps {
  courseId?: string | number;
  noDataPlaceholder?: React.ReactNode;
  onEventClick?: (event: CalendarEvent) => void;
}

export const EditCourseSchedulePreview = ({
  courseId,
  noDataPlaceholder,
  onEventClick,
}: CourseSchedulePreviewProps) => {
  const {
    state: {
      schedule: { refresh: refreshFromContext },
    },
  } = useCourseCreate();
  const [previewType, setPreviewType] = useState<CourseSchedulePreviewType>("Lista");
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [addNewModalIsOpen, setAddNewModalIsOpen] = useState(false);
  const [bulkEditModalIsOpen, setBulkEditModalIsOpen] = useState(false);
  const [bulkDeleteModalIsOpen, setBulkDeleteModalIsOpen] = useState(false);
  const [editCourseSchedule, setEditCourseSchedule] = React.useState<ScheduleModel | undefined>(undefined);
  const [exportModalIsOpen, setExportModalIsOpen] = useState(false);
  const [historyModalIsOpen, setHistoryModalIsOpen] = useState(false);
  const [tableKey, setTableKey] = useState(0);
  const { course } = useCourseQuery(courseId);
  const { showInfo, showError } = useToaster();
  const queryClient = useQueryClient();

  const { schedule, isLoading, invalidate } = useScheduleEvents(courseId!);
  const { conflicts, byScheduleId } = useScheduleConflicts(Number(courseId));
  const describe = React.useCallback(
    (c: Parameters<typeof describeConflict>[0]) => describeConflict(c, Number(courseId)),
    [courseId],
  );

  // Trainer hours and cost entries are derived from the schedule (the API recalculates cost entries
  // on read), so every schedule change must refetch them too.
  const invalidateTrainerData = () => {
    queryClient.invalidateQueries({ queryKey: ["fetchTrainersByCourseId"] });
    queryClient.invalidateQueries({ queryKey: QUERIES_KEYS.TRAINER_COST_ENTRIES(Number(courseId)) });
    queryClient.invalidateQueries({ queryKey: SCHEDULE_CONFLICTS_KEY(Number(courseId)) });
  };

  useEffect(() => {
    invalidate();
    invalidateTrainerData();
  }, [refreshFromContext]);

  // Errors propagate to CourseScheduleList, which shows the toast.
  const deleteEvent = async (courseScheduleId: number) => {
    await courseschedulesApi.delete(Number(courseId), courseScheduleId);
    invalidateAfterDelete();
  };

  const invalidateAfterDelete = () => {
    invalidate();
    invalidateTrainerData();
  };

  // A row deleted via its trash icon may still be in the table's selection - never send it to bulk actions
  // (the bulk endpoints reject the whole request when any id is missing).
  const validSelectedRows = useMemo(
    () => selectedRows.filter((id) => schedule?.events.some((event) => event.id === id)),
    [selectedRows, schedule],
  );

  const bulkDeleteEvents = async () => {
    try {
      await courseschedulesApi.bulkDelete(Number(courseId), { courseScheduleIds: validSelectedRows });
      showInfo("Pomyślnie usunięto wybrane wydarzenia.");
      setBulkDeleteModalIsOpen(false);
      invalidateAfterDelete();
      setSelectedRows([]);
      setTableKey((prev) => prev + 1);
    } catch (error) {
      showError("Wystąpił błąd podczas usuwania wybranych wydarzeń.");
      console.error(error);
    }
  };

  if (isLoading) return null;
  if (!schedule) return null;

  return (
    <PaziaBox className="col-span-12 flex flex-col pl-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <ButtonSwitch
          a="Lista"
          b="Kalendarz"
          value="Lista"
          onChange={(x) => setPreviewType(x as CourseSchedulePreviewType)}
          className="mb-4 mt-2"
        />
        <div className="flex flex-wrap gap-2 sm:gap-4">
          <MoreActionsMenu
            disabled={validSelectedRows.length < 1}
            actions={[
              {
                name: "Zmień miejsce",
                onClick: () => setBulkEditModalIsOpen(true),
              },
              {
                name: "Usuń wybrane",
                onClick: () => setBulkDeleteModalIsOpen(true),
              },
            ]}
          >
            Edytuj wybrane
          </MoreActionsMenu>
          <PaziaButton type="secondary" onClick={() => setHistoryModalIsOpen(true)}>
            Historia
          </PaziaButton>
          <PaziaButton type="secondary" onClick={() => setExportModalIsOpen(true)}>
            Eksportuj
          </PaziaButton>
          <PaziaButton type="primary" onClick={() => setAddNewModalIsOpen(true)}>
            Dodaj
          </PaziaButton>
        </div>
      </div>
      <>
        {conflicts.length > 0 && (
          <div
            data-testid="schedule-conflicts-notice"
            className="mb-3 rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800 dark:border-rose-500/40 dark:bg-rose-500/10 dark:text-rose-200"
          >
            <div className="font-semibold">
              Kolizje w harmonogramie ({byScheduleId.size} {byScheduleId.size === 1 ? "pozycja" : "pozycje"}): trener
              lub sala zajęci w tym samym czasie
            </div>
            <ul className="mt-1 list-disc pl-5">
              {conflicts.slice(0, 5).map((c, i) => (
                <li key={i}>{describe(c)}</li>
              ))}
              {conflicts.length > 5 && <li>…i {conflicts.length - 5} więcej</li>}
            </ul>
          </div>
        )}
        {previewType === "Lista" ? (
          <CourseScheduleList
            key={tableKey}
            onSelectionChange={setSelectedRows}
            schedules={schedule.events}
            deleteEvent={deleteEvent}
            editEvent={async (courseSchedule: ScheduleModel) => setEditCourseSchedule(courseSchedule)}
            conflicts={byScheduleId}
            describeConflict={describe}
          />
        ) : (
          <CourseSchedulePreview
            noDataPlaceholder={noDataPlaceholder}
            onEventClick={onEventClick}
            schedules={schedule.events}
          />
        )}

        <EditCourseScheduleModal
          course={course}
          courseSchedule={editCourseSchedule}
          onCloseRequest={async () => setEditCourseSchedule(undefined)}
        />
        <CreateNewEventModal
          course={course}
          courseId={course.data.id}
          isOpen={addNewModalIsOpen}
          onCloseRequest={() => setAddNewModalIsOpen(false)}
        />
        <ExportScheduleModal
          isOpen={exportModalIsOpen}
          onCloseRequest={() => setExportModalIsOpen(false)}
          events={schedule.events}
          courseId={Number(courseId)}
          courseName={course.data?.name}
          selectedScheduleIds={validSelectedRows}
        />
        <ScheduleHistoryModal
          isOpen={historyModalIsOpen}
          onCloseRequest={() => setHistoryModalIsOpen(false)}
          courseId={Number(courseId)}
        />
        <BulkEditPlacesModal
          isOpen={bulkEditModalIsOpen}
          onCloseRequest={() => setBulkEditModalIsOpen(false)}
          courseId={Number(courseId)}
          selectedScheduleIds={validSelectedRows}
          onSuccess={() => {
            invalidate();
            setSelectedRows([]);
            setTableKey((prev) => prev + 1); // Force table to re-render and clear selection
          }}
        />
        <PaziaDeleteModal
          id="bulk-delete-schedule-modal"
          title="Usuń wybrane wydarzenia"
          description={`Czy na pewno chcesz usunąć ${validSelectedRows.length} wybranych wydarzeń z harmonogramu?`}
          isOpen={bulkDeleteModalIsOpen}
          onCloseRequest={() => setBulkDeleteModalIsOpen(false)}
          onConfirm={bulkDeleteEvents}
        />
      </>
    </PaziaBox>
  );
};
